import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { createManualPrice } from "../../../services/ImportPrice";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    minWidth: "500px",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
}));

function ManualPriceDialog({ open, onClose, selectedItem, onSuccess }) {
  const [formData, setFormData] = useState({
    price: selectedItem?.currentPrice || "",
    note: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Get current user (you might need to adjust this based on your auth implementation)
  const getCurrentUser = () => {
    // This should come from your auth context/store
    const user = localStorage.getItem("username") || "admin";
    return user;
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.price || formData.price <= 0) {
      newErrors.price = "Price must be greater than 0";
    }

    if (formData.price && isNaN(parseFloat(formData.price))) {
      newErrors.price = "Price must be a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody = {
        importId: selectedItem.importId,
        currentPrice: parseFloat(formData.price),
        note:
          formData.note || `Manual price update for ${selectedItem.itemName}`,
        updatedBy: getCurrentUser(),
      };

      await createManualPrice(requestBody);

      setSubmitSuccess(true);

      // Close dialog after a short delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error updating price:", error);
      setSubmitError(
        error.response?.data?.error ||
          error.message ||
          "Failed to update price. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate price difference
  const priceDifference = selectedItem?.currentPrice
    ? parseFloat(formData.price || 0) - selectedItem.currentPrice
    : 0;

  const percentageChange =
    selectedItem?.currentPrice && formData.price
      ? ((priceDifference / selectedItem.currentPrice) * 100).toFixed(2)
      : 0;

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <EditIcon fontSize="large" />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Update Price
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {selectedItem?.itemName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Current Price Display */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 1,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Current Price
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="primary">
            Rs.{selectedItem?.currentPrice?.toFixed(2) || "N/A"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Product Code: {selectedItem?.productCode}
          </Typography>
        </Box>

        {/* Error Alert */}
        {submitError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        {/* Success Alert */}
        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Price updated successfully!
          </Alert>
        )}

        {/* New Price Input */}
        <TextField
          fullWidth
          label="New Price"
          name="price"
          type="number"
          value={formData.price}
          onChange={handleChange}
          onWheel={(e) => e.target.blur()}
          error={!!errors.price}
          helperText={errors.price}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoneyIcon color="action" />
              </InputAdornment>
            ),
          }}
          inputProps={{
            step: "0.01",
            min: "0",
          }}
          sx={{ mb: 2 }}
        />

        {/* Price Change Indicator */}
        {formData.price && selectedItem?.currentPrice && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              backgroundColor:
                priceDifference > 0
                  ? "rgba(244, 67, 54, 0.08)"
                  : priceDifference < 0
                  ? "rgba(76, 175, 80, 0.08)"
                  : "rgba(0, 0, 0, 0.02)",
              border: `1px solid ${
                priceDifference > 0
                  ? "rgba(244, 67, 54, 0.3)"
                  : priceDifference < 0
                  ? "rgba(76, 175, 80, 0.3)"
                  : "rgba(0, 0, 0, 0.1)"
              }`,
            }}
          >
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Price Change
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={
                  priceDifference > 0
                    ? "error"
                    : priceDifference < 0
                    ? "success"
                    : "textPrimary"
                }
              >
                {priceDifference > 0 ? "+" : ""}Rs.{priceDifference.toFixed(2)}
              </Typography>
              <Typography
                variant="body2"
                color={
                  priceDifference > 0
                    ? "error"
                    : priceDifference < 0
                    ? "success"
                    : "textSecondary"
                }
              >
                ({percentageChange > 0 ? "+" : ""}
                {percentageChange}%)
              </Typography>
            </Box>
          </Box>
        )}

        {/* Notes Input */}
        <TextField
          fullWidth
          label="Notes (Optional)"
          name="note"
          value={formData.note}
          onChange={handleChange}
          multiline
          rows={3}
          placeholder="Add any notes about this price change..."
          helperText="Provide context for this price update"
        />

        {/* Info Box */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: "rgba(25, 118, 210, 0.08)",
            border: "1px solid rgba(25, 118, 210, 0.2)",
          }}
        >
          <Typography variant="caption" color="primary">
            <strong>Note:</strong> This will create a new price record with type
            "MANUALLY_DEFINED" and set it as the current price for this item.
            The previous price will be archived in the price history.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || submitSuccess}
        >
          {isSubmitting
            ? "Updating..."
            : submitSuccess
            ? "Updated!"
            : "Update Price"}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}

export default ManualPriceDialog;
